import * as React from 'react';

import { SalesOrderLineItem } from '@rematter/frontend-lib-api-client';
import { useAtomValue } from 'jotai';

import {
  $salesOrderLineItems,
  $loadForm,
} from '~/modules/trade/_state/loadForm.state';
import { LoadFormLineItem } from '~/modules/trade/types/load';
import { toMoney } from '~/modules/trade/utils/money';
import { convertSalesOrderQuantityToWeight } from '~/modules/trade/utils/weight';

export const useSalesOrderLineItemsData = () => {
  const form = useAtomValue($loadForm);

  const { data, isLoading: isLoadingSalesOrderLineItems } =
    useAtomValue($salesOrderLineItems);

  const salesOrderLineItemsData = React.useMemo(
    () =>
      (data?.salesOrders.entries[0]?.lineItems ?? []) as SalesOrderLineItem[],
    [data],
  );

  const originalSalesOrderLineItems = React.useMemo(() => {
    if (!form.salesOrderId) return [];
    if (!salesOrderLineItemsData || isLoadingSalesOrderLineItems) return [];

    return salesOrderLineItemsData.map((item) => {
      const convertedWeight = convertSalesOrderQuantityToWeight(
        Number(item.quantity),
        item.unit,
      );

      return {
        itemId: item.item.id,
        itemName: item.item.name,
        unitPrice: toMoney(item.unitPrice),
        pricingUnit: item.unit,
        quantityOwedWeight: convertedWeight,
      };
    });
  }, [
    form.salesOrderId,
    salesOrderLineItemsData,
    isLoadingSalesOrderLineItems,
  ]);

  const originalSalesOrderLineItemsMap = React.useMemo(
    () =>
      new Map(originalSalesOrderLineItems.map((item) => [item.itemId, item])),
    [originalSalesOrderLineItems],
  );

  const getSeparatedLineItems = React.useCallback(
    (formLineItems: LoadFormLineItem[]) => {
      const salesOrderLineItems = formLineItems
        .filter(
          (item) =>
            (item.itemId && originalSalesOrderLineItemsMap.has(item.itemId)) ||
            item.pendingSalesOrderMaterialSelection === true,
        )
        .filter(
          (item) =>
            !item.id.startsWith('temp-') || item.includeInSalesOrder === true,
        )
        .map((item) => ({
          ...item,
          ...(item.itemId
            ? originalSalesOrderLineItemsMap.get(item.itemId)
            : {}),
        }));

      const salesOrderLineItemsMap = new Set(
        salesOrderLineItems.map((item) => item.id),
      );

      const standaloneLineItems = formLineItems.filter(
        (item) => !salesOrderLineItemsMap.has(item.id),
      );

      return {
        salesOrderLineItems,
        standaloneLineItems,
      };
    },
    [originalSalesOrderLineItemsMap],
  );

  const getRemovedSalesOrderLineItems = React.useCallback(
    (formLineItems: LoadFormLineItem[]) => {
      if (!originalSalesOrderLineItemsMap.size) return [];

      const currentItemIds = new Set(
        formLineItems.filter((item) => item.itemId).map((item) => item.itemId),
      );

      return Array.from(originalSalesOrderLineItemsMap.values()).filter(
        (item) => !currentItemIds.has(item.itemId),
      );
    },
    [originalSalesOrderLineItemsMap],
  );

  return {
    salesOrderLineItemsData,
    isLoading: isLoadingSalesOrderLineItems,
    originalSalesOrderLineItems,
    originalSalesOrderLineItemsMap,
    getSeparatedLineItems,
    getRemovedSalesOrderLineItems,
  };
};
